import React, { useState, useRef } from "react";
import {
    Stack,
    IStackTokens,
    TextField,
    PrimaryButton,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    Label,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    Text,
    SearchBox,
} from "@fluentui/react";
import { _Styles } from "../../content/styles/Page.styles";
import { nextGenService } from "../../services/NextGenService";
import { getUserSession, getLoggedinUserRoleDetails, downloadDatabyInputFormat } from "../../components/shared/common/util";
import { IOpportunityAccount } from "../../models/IOpportunityAccount";
import { IAccount } from "../../models/IAccount";
import { PartnerType } from "../../models/PartnerType";
import { DealType } from "../../models/DealType";
import { IDealState } from "../pricingprogram/common/state/IDealState";
import { UserRole } from "../../models/UserRole";
import { AccountAllType } from "../../models/AccountAllType";

/* ─── Role options ───────────────────────────────────────── */
const ROLE_OPTIONS: IDropdownOption[] = [
    { key: "ADD", text: "ADD" },
    { key: "ADR", text: "ADR" },
    { key: "DMP", text: "DMP" },
    { key: "MSS", text: "MSS" },
];

const partnerTypeForRole: Record<string, PartnerType[]> = {
    ADD: [PartnerType.ADD],
    ADR: [PartnerType.ADR],
    DMP: [PartnerType.DMP],
    MSS: [PartnerType.MsStore],
};

/* ─── Helpers ────────────────────────────────────────────── */
function buildAccountLabel(account: IAccount): string {
    const parts: string[] = [account.accountName || account.companyName || ""];
    if (account.organizationId || account.locationId) {
        parts.push(`(#MPN ORG/LOC: ${account.organizationId || ""}/${account.locationId || ""})`);
    }
    if (account.countryName) parts.push(`[${account.countryName}]`);
    if (account.productType) parts.push(`· ${account.productType}`);
    return parts.join(" ").trim();
}

/* Filters a raw accounts list to those matching the country and/or partner type constraints. */
function filterFetchedAccounts(
    accounts: IAccount[],
    countryIds: string[],
    includeAllEuefta: boolean,
    targetType: PartnerType
): IAccount[] {
    return accounts.filter((a) => {
        const typeOk = a.partnerType === undefined || (a.partnerType as PartnerType) === targetType;
        if (!typeOk) return false;
        if (includeAllEuefta) return true;          // AllEuefta: no country filter
        if (!countryIds.length) return true;        // no restriction recorded
        return countryIds.includes(a.countryId ?? "") || countryIds.includes(a.country ?? "");
    });
}

function flattenAccounts(opportunityAccounts: IOpportunityAccount[], selectedRole: string): IAccount[] {
    const allowedTypes = partnerTypeForRole[selectedRole] ?? [];
    const seen = new Set<string>();
    const result: IAccount[] = [];

    const pushIfMatch = (acc: IAccount, fallbackCountryId?: string, fallbackCountryName?: string) => {
        const effectiveType: PartnerType | undefined =
            acc.partnerType !== undefined ? (acc.partnerType as PartnerType) : undefined;
        // Key on accountId+productTypeId so the same account under different product types stays distinct.
        const dedupKey = acc.accountId;
        if (effectiveType !== undefined && allowedTypes.includes(effectiveType) && acc.accountId && !seen.has(dedupKey)) {
            seen.add(dedupKey);
            result.push({
                ...acc,
                accountName: acc.accountName || (acc as any).companyName || "",
                countryId: acc.countryId ?? fallbackCountryId,
                countryName: acc.countryName ?? fallbackCountryName,
            });
        }
    };

    opportunityAccounts.forEach((oa) => {
        (oa.accounts ?? []).forEach((acc) => pushIfMatch(acc, oa.countryId, oa.countryName));
        if (oa.partnerAccounts) {
            Object.values(oa.partnerAccounts).forEach((nested) =>
                (nested ?? []).forEach((acc) => pushIfMatch(acc, oa.countryId, oa.countryName))
            );
        }
    });
    return result;
}

/* ─── Styles ─────────────────────────────────────────────── */
const wrap: React.CSSProperties = { maxWidth: 860 };

const card: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e1dfdd",
    borderRadius: 6,
    padding: "24px 28px",
    marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const stepRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
};

const stepBadge = (active: boolean): React.CSSProperties => ({
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: active ? "#0078d4" : "#c8c6c4",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
});

const stepTitle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: "#323130",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
};

const divider: React.CSSProperties = {
    borderTop: "1px solid #e1dfdd",
    margin: "20px 0",
};

const chip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#f3f9ff",
    border: "1px solid #c7e0f4",
    borderRadius: 4,
    padding: "5px 12px",
    fontSize: 13,
    color: "#0078d4",
    marginTop: 16,
};

const chipLabel: React.CSSProperties = { color: "#605e5c", marginRight: 2 };

const twoCol: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
};

const downloadCard: React.CSSProperties = {
    background: "#f8f8f8",
    border: "1px solid #e1dfdd",
    borderRadius: 6,
    padding: "18px 22px",
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
};

const rowGap: IStackTokens = { childrenGap: 10 };
const fieldGap: IStackTokens = { childrenGap: 16 };

/* ─── Component ─────────────────────────────────────────── */
export const SelfServeTool: React.FC = () => {
    const [caseNumber, setCaseNumber] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchDone, setSearchDone] = useState(false);
    const [searchedCaseNumber, setSearchedCaseNumber] = useState("");

    const [deals, setDeals] = useState<IDealState[]>([]);
    const [allOpportunityAccounts, setAllOpportunityAccounts] = useState<IOpportunityAccount[]>([]);

    const [selectedDeal, setSelectedDeal] = useState<IDealState | null>(null);
    const [opportunityAccounts, setOpportunityAccounts] = useState<IOpportunityAccount[]>([]);
    const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

    const [selectedRole, setSelectedRole] = useState("");
    const [availableAccounts, setAvailableAccounts] = useState<IAccount[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState("");
    const [accountSearchText, setAccountSearchText] = useState("");
    const [isIncludeSku, setIsIncludeSku] = useState(true);

    const [isDownloading, setIsDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Broad-selection (All / AllEuefta) state — fetched once in handleRoleChange, filtered client-side
    const [isBroadSelection, setIsBroadSelection] = useState(false);
    const [baseAccounts, setBaseAccounts] = useState<IAccount[]>([]);
    const broadCacheRef = useRef<IAccount[]>([]);

    const clearState = () => {
        setDeals([]); setAllOpportunityAccounts([]); setSelectedDeal(null);
        setOpportunityAccounts([]); setSelectedRole(""); setAvailableAccounts([]);
        setSelectedAccountId(""); setAccountSearchText(""); setErrorMessage("");
        setSuccessMessage(""); setSearchDone(false); setSearchedCaseNumber("");
        setIsBroadSelection(false); setBaseAccounts([]); broadCacheRef.current = [];
    };

    /* ── Step 1: search ── */
    const handleSearch = async () => {
        const trimmed = caseNumber.trim();
        if (!trimmed) { setErrorMessage("Please enter a case number."); return; }
        clearState();
        setIsSearching(true);
        try {
            const response = await nextGenService.getOpportunityCommon(trimmed, getLoggedinUserRoleDetails(), "");
            if (response.status === 200 && response.data?.opportunityDeals?.length > 0) {
                setDeals(response.data.opportunityDeals);
                setAllOpportunityAccounts(response.data.opportunityAccounts ?? []);
                setSearchedCaseNumber(trimmed);
                setSearchDone(true);
            } else {
                setErrorMessage(`No deals found for case number: ${trimmed}`);
                setSearchDone(true);
            }
        } catch (err: any) {
            setErrorMessage(err?.response?.status === 404 ? `Case not found: ${trimmed}` : "An error occurred while searching. Please try again.");
            setSearchDone(true);
        } finally {
            setIsSearching(false);
        }
    };

    /* ── Step 2a: deal selected ── */
    const handleDealChange = async (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (!option) return;
        const deal = deals.find((d) => d.id === option.key);
        if (!deal) return;
        setSelectedDeal(deal); setSelectedRole(""); setAvailableAccounts([]);
        setOpportunityAccounts([]); setSelectedAccountId(""); setAccountSearchText("");
        setSuccessMessage(""); setErrorMessage("");
        setIsLoadingAccounts(true);
        try {
            const response = await nextGenService.getOpportunityCommon(searchedCaseNumber, getLoggedinUserRoleDetails(), deal.name ?? "");
            setOpportunityAccounts(response.status === 200 ? (response.data?.opportunityAccounts ?? []) : allOpportunityAccounts);
        } catch {
            setOpportunityAccounts(allOpportunityAccounts);
        } finally {
            setIsLoadingAccounts(false);
        }
    };

    /* ── Step 2b: role selected ── */
    const handleRoleChange = async (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (!option) return;
        const role = option.key as string;
        setSelectedRole(role); setSelectedAccountId(""); setAccountSearchText("");
        setSuccessMessage(""); setErrorMessage("");
        setAvailableAccounts([]);

        // Specific accounts come from isAllSelected === None entries.
        const specificAccounts = flattenAccounts(opportunityAccounts, role);

        // Check for All / AllEuefta entries — these have no accounts[] so flattenAccounts misses them.
        const allowedTypes = partnerTypeForRole[role] ?? [];
        // Reseller (5) is a combined ADR+DMP container; include it when either role is selected.
        const broadTypes = (role === "ADR" || role === "DMP")
            ? [...allowedTypes, PartnerType.Reseller]
            : allowedTypes;
        const broadEntries = opportunityAccounts.filter(
            (oa) =>
                oa.partnerType !== undefined &&
                broadTypes.includes(oa.partnerType as PartnerType) &&
                (oa.isAllSelected === AccountAllType.All || oa.isAllSelected === AccountAllType.AllEuefta)
        );

        if (broadEntries.length === 0) {
            setIsBroadSelection(false);
            setBaseAccounts(specificAccounts);
            setAvailableAccounts(specificAccounts);
            return;
        }

        // Fetch all active accounts for this role once; search/filter will be client-side.
        const countryIds = broadEntries
            .filter((e) => e.isAllSelected === AccountAllType.All && e.countryId)
            .map((e) => e.countryId as string);
        const euefta = broadEntries.some((e) => e.isAllSelected === AccountAllType.AllEuefta);
        const target = role === "ADD" ? PartnerType.ADD : role === "DMP" ? PartnerType.DMP : PartnerType.ADR;

        setIsLoadingAccounts(true);
        try {
            const resp = target === PartnerType.ADD
                ? await nextGenService.getAllDistributors()
                : await nextGenService.getAllResellers();
            const seenIds = new Set<string>();
            broadCacheRef.current = filterFetchedAccounts(resp.data ?? [], countryIds, euefta, target)
                .filter((a) => { if (seenIds.has(a.accountId)) return false; seenIds.add(a.accountId); return true; });
        } catch {
            broadCacheRef.current = [];
        } finally {
            setIsLoadingAccounts(false);
        }

        setIsBroadSelection(true);
        setBaseAccounts(specificAccounts);
        setAvailableAccounts(specificAccounts);
    };

    /* ── Derived ── */
    const filteredAccounts = (!isBroadSelection && accountSearchText)
        ? availableAccounts.filter((a) => buildAccountLabel(a).toLowerCase().includes(accountSearchText.toLowerCase()))
        : availableAccounts;

    /* Pure client-side filter against the pre-fetched cache — no async, no race conditions. */
    const handleBroadSearch = (text: string) => {
        setAccountSearchText(text);
        if (text.length < 2) { setAvailableAccounts(baseAccounts); return; }
        const lower = text.toLowerCase();
        const matches = broadCacheRef.current
            .filter((a) => buildAccountLabel(a).toLowerCase().includes(lower))
            .slice(0, 100);
        const seen = new Set(matches.map((a) => a.accountId));
        setAvailableAccounts([...matches, ...baseAccounts.filter((a) => !seen.has(a.accountId))]);
    };

    const selectedAccount = availableAccounts.find((a) => a.accountId === selectedAccountId);
    const isDownloadReady = !!selectedDeal && !!selectedRole && !!selectedAccountId;

    const dealOptions: IDropdownOption[] = deals.map((d) => ({
        key: d.id ?? "",
        text: `${d.dealType?.name ?? "Unknown"} \u2013 ${d.name ?? ""}`,
    }));

    /* ── Step 3: download ── */
    const handleDownload = async () => {
        if (!selectedDeal || !selectedRole || !selectedAccountId || !selectedAccount) return;
        setIsDownloading(true); setErrorMessage(""); setSuccessMessage("");
        try {
            const loggedInUserInfo = JSON.parse(getUserSession()!);
            const pricingProgram = selectedDeal.dealType?.name ?? "";
            const filter = JSON.stringify({
                logic: "and",
                filters: [{ logic: "and", filters: [{ field: "name", operator: "eq", value: selectedDeal.name ?? "" }] }],
            });
            const dashboardRequest = {
                accountsIds: selectedAccount.accountId,
                userRoles: selectedRole,
                userRoleDetails: [{ accountId: selectedAccount.accountId, countryId: selectedAccount.countryId ?? "", applicablePriceListGeo: "", userRole: selectedRole === "MSS" ? UserRole.MSStore : UserRole.Channel, userSubRole: selectedRole, isLoggedInUserOrg: true }],
                dealType: pricingProgram,
                email: loggedInUserInfo.email,
                isLocalEmpowerment: false,
                tpiDs: [],
                filter,
                pageNumber: 1,
                recordsToFetch: 1000,
            };
            const response = await nextGenService.getSelfServeCaseLetters(selectedDeal.id ?? "", pricingProgram, selectedRole, dashboardRequest, isIncludeSku);
            const dealTypeLower = pricingProgram.toLowerCase();
            const resellerName = selectedAccount.accountName || selectedAccount.companyName || "";
            const fileName = (dealTypeLower === DealType.DealRegistration.toLowerCase() || dealTypeLower === DealType.SpecialPricing.toLowerCase())
                ? `Case Decision - ${searchedCaseNumber}${resellerName ? " - " + resellerName : ""}.pdf`
                : `Case Decision - ${searchedCaseNumber}.pdf`;
            downloadDatabyInputFormat(fileName.replace(/[/:*?"<>|]/g, ""), response);
            setSuccessMessage(`Decision letter downloaded successfully for case ${searchedCaseNumber}.`);
        } catch (err: any) {
            const status = err?.response?.status;
            setErrorMessage(
                status === 404 ? "Decision letter not found for the selected case and partner combination."
                : status === 403 ? "Access denied. You do not have permission to download this letter."
                : `Failed to download decision letter. ${status ? `(${status})` : "Please try again."}`
            );
        } finally {
            setIsDownloading(false);
        }
    };

    /* ── Render ── */
    return (
        <div className={_Styles.rootDiv}>
            <div style={wrap}>

                {/* Page title */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#323130", marginBottom: 4 }}>
                        Self-Serve Tool
                    </div>
                    <div style={{ fontSize: 13, color: "#605e5c" }}>
                        Download a case decision letter on behalf of any partner. Only available to authorised Microsoft staff.
                    </div>
                </div>

                {/* ── Step 1: Find Case ─────────────────────── */}
                <div style={card}>
                    <div style={stepRow}>
                        <div style={stepBadge(true)}>1</div>
                        <span style={stepTitle}>Find Case</span>
                    </div>

                    <Stack horizontal tokens={rowGap} verticalAlign="end" styles={{ root: { maxWidth: 580 } }}>
                        <Stack.Item grow={1}>
                            <Label>Case Number</Label>
                            <TextField
                                placeholder="e.g. CAS-015207-P0G1N3"
                                value={caseNumber}
                                onChange={(_, v) => { setCaseNumber(v ?? ""); setErrorMessage(""); }}
                                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                                disabled={isSearching}
                            />
                        </Stack.Item>
                        <Stack.Item>
                            <PrimaryButton
                                text={isSearching ? "Searching…" : "Search"}
                                onClick={handleSearch}
                                disabled={isSearching || !caseNumber.trim()}
                                iconProps={{ iconName: "Search" }}
                            />
                        </Stack.Item>
                        {searchDone && (
                            <Stack.Item>
                                <DefaultButton text="Clear" onClick={() => { setCaseNumber(""); clearState(); }} />
                            </Stack.Item>
                        )}
                    </Stack>

                    {isSearching && (
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{ marginTop: 14 }}>
                            <Spinner size={SpinnerSize.small} />
                            <Text variant="small">Searching…</Text>
                        </Stack>
                    )}

                    {/* Case found chip */}
                    {searchDone && deals.length > 0 && (
                        <div style={chip}>
                            <span style={{ fontSize: 14 }}>✓</span>
                            <span><span style={chipLabel}>Case:</span><strong>{searchedCaseNumber}</strong></span>
                            <span style={{ color: "#c8c6c4" }}>|</span>
                            <span><span style={chipLabel}>Deals found:</span><strong>{deals.length}</strong></span>
                        </div>
                    )}

                    {errorMessage && (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage("")} styles={{ root: { marginTop: 14 } }}>
                            {errorMessage}
                        </MessageBar>
                    )}
                    {successMessage && (
                        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccessMessage("")} styles={{ root: { marginTop: 14 } }}>
                            {successMessage}
                        </MessageBar>
                    )}
                </div>

                {/* ── Steps 2 & 3 ── */}
                {searchDone && deals.length > 0 && (
                    <div style={card}>
                        <div style={stepRow}>
                            <div style={stepBadge(true)}>2</div>
                            <span style={stepTitle}>Select Deal &amp; Partner</span>
                        </div>

                        {/* Deal + Role row */}
                        <div style={twoCol}>
                            <div>
                                <Label required>Deal / Pricing Program</Label>
                                <Dropdown
                                    placeholder="Select a deal"
                                    options={dealOptions}
                                    selectedKey={selectedDeal?.id ?? null}
                                    onChange={handleDealChange}
                                />
                            </div>
                            <div>
                                <Label required>Partner Role</Label>
                                {isLoadingAccounts ? (
                                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{ marginTop: 8 }}>
                                        <Spinner size={SpinnerSize.small} />
                                        <Text variant="small">Loading accounts…</Text>
                                    </Stack>
                                ) : (
                                    <Dropdown
                                        placeholder={selectedDeal ? "Select a role" : "Select a deal first"}
                                        options={ROLE_OPTIONS}
                                        selectedKey={selectedRole || null}
                                        onChange={handleRoleChange}
                                        disabled={!selectedDeal || isLoadingAccounts}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Partner account */}
                        {selectedDeal && selectedRole && !isLoadingAccounts && (
                            <>
                                <div style={divider} />
                                {/* Broad selection starts with empty availableAccounts intentionally — don't treat that as "none found" */}
                                {availableAccounts.length === 0 && !isBroadSelection ? (
                                    <MessageBar messageBarType={MessageBarType.warning}>
                                        No <strong>{selectedRole}</strong> accounts found for this deal.
                                    </MessageBar>
                                ) : (
                                    <Stack tokens={fieldGap}>
                                        <div>
                                            {isBroadSelection ? (
                                                // Accounts pre-fetched; search filters client-side
                                                <>
                                                    <Label required styles={{ root: { marginBottom: 4 } }}>Partner Account</Label>
                                                    <Text variant="small" style={{ color: "#605e5c", display: "block", marginBottom: 6 }}>
                                                        {`${broadCacheRef.current.length.toLocaleString()} accounts loaded. Type to filter (max 100 shown).`}
                                                        {accountSearchText.length >= 2 && ` ${filteredAccounts.length} match${filteredAccounts.length !== 1 ? "es" : ""}.`}
                                                    </Text>
                                                    <TextField
                                                        placeholder="Search by account name…"
                                                        value={accountSearchText}
                                                        onChange={(_, v) => handleBroadSearch(v ?? "")}
                                                        styles={{ root: { maxWidth: 400, marginBottom: 6 } }}
                                                        iconProps={{ iconName: "Search" }}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center" style={{ marginBottom: 6 }}>
                                                        <Label required styles={{ root: { margin: 0 } }}>Partner Account</Label>
                                                        <Text variant="small" style={{ color: "#605e5c" }}>({filteredAccounts.length} of {availableAccounts.length})</Text>
                                                    </Stack>
                                                    <SearchBox
                                                        placeholder="Filter by name…"
                                                        value={accountSearchText}
                                                        onChange={(_, v) => setAccountSearchText(v ?? "")}
                                                        underlined
                                                        styles={{ root: { maxWidth: 360, marginBottom: 6 } }}
                                                    />
                                                </>
                                            )}
                                            <Dropdown
                                                placeholder={`Select a ${selectedRole} account`}
                                                options={filteredAccounts.map((acc) => ({ key: acc.accountId, text: buildAccountLabel(acc) }))}
                                                selectedKey={selectedAccountId || null}
                                                onChange={(_, opt) => { if (opt) { setSelectedAccountId(opt.key as string); setSuccessMessage(""); setErrorMessage(""); } }}
                                            />
                                        </div>

                                        {/* Letter type — DR / SP only */}
                                        {(selectedDeal.dealType?.name === DealType.DealRegistration || selectedDeal.dealType?.name === DealType.SpecialPricing) && (
                                            <div style={{ maxWidth: 280 }}>
                                                <Label>Letter Type</Label>
                                                <Dropdown
                                                    options={[
                                                        { key: "true", text: "With SKU details" },
                                                        { key: "false", text: "Without SKU details" },
                                                    ]}
                                                    selectedKey={isIncludeSku ? "true" : "false"}
                                                    onChange={(_, opt) => setIsIncludeSku(opt?.key === "true")}
                                                />
                                            </div>
                                        )}
                                    </Stack>
                                )}
                            </>
                        )}

                        {/* ── Step 3: Download ── */}
                        {isDownloadReady && (
                            <>
                                <div style={divider} />
                                <div style={stepRow}>
                                    <div style={stepBadge(true)}>3</div>
                                    <span style={stepTitle}>Download</span>
                                </div>
                                <div style={downloadCard}>
                                    <PrimaryButton
                                        text={isDownloading ? "Generating…" : "Download Decision Letter"}
                                        onClick={handleDownload}
                                        disabled={isDownloading}
                                        iconProps={{ iconName: "Download" }}
                                        styles={{ root: { minWidth: 220 } }}
                                    />
                                    {isDownloading
                                        ? <Spinner size={SpinnerSize.small} label="Generating…" labelPosition="right" />
                                        : (
                                            <Text variant="small" style={{ color: "#605e5c" }}>
                                                Role:&nbsp;<strong>{selectedRole}</strong>
                                                &ensp;·&ensp;
                                                Account:&nbsp;<strong>{selectedAccount?.accountName || selectedAccount?.companyName}</strong>
                                                &ensp;·&ensp;
                                                Case:&nbsp;<strong>{searchedCaseNumber}</strong>
                                            </Text>
                                        )
                                    }
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* No results */}
                {searchDone && deals.length === 0 && !errorMessage && (
                    <div style={card}>
                        <MessageBar messageBarType={MessageBarType.info}>
                            No deals found for <strong>{caseNumber}</strong>. Verify the case number and try again.
                        </MessageBar>
                    </div>
                )}
            </div>
        </div>
    );
};
